import { useEffect } from 'react'
import PropTypes from 'prop-types'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { ToggleSwitch } from '@/components/ui/toggle-switch'
import { useDesignVersionToggle } from '@/hooks'

import Toolbar from '../Toolbar/Toolbar'
import { selectActiveBuilding } from '../../reducers/buildingSelectorReducer'
import { selectUserRole } from '../../reducers/userDataReducer'

const MaintenanceToolbar = ({ toggleModal }) => {
  const { t } = useTranslation(['maintenance'])
  const navigate = useNavigate()
  const location = useLocation()
  const { requestId: paramsRequestId, buildingId: paramsBuildingId } = useParams()

  const userRole = useSelector(selectUserRole)
  const selectedBuilding = useSelector(selectActiveBuilding)

  // Note: only the Requests tab has a toggle - Staff's own table/content is unchanged between
  // old and new design (only the surrounding toolbar chrome differs), so a toggle there wouldn't
  // switch anything visible.
  const isStaffTab = location.pathname.includes('/staff')
  const toggleBuildingId = paramsBuildingId || (selectedBuilding && selectedBuilding.id) || ''

  const { toggle: handleUIToggle } = useDesignVersionToggle({
    group: 'maintenance',
    screen: 'MaintenanceRequests',
    to: {
      params: { buildingId: String(toggleBuildingId) },
      path: '/dashboard/building-dashboard/maintenance/requests/:buildingId',
    },
    version: 'legacy',
  })

  // Note: this toolbar is only mounted under the old-design `/maintenance/legacy` route (see
  // EL-14186 rework) - it must keep that prefix when syncing/self-linking, otherwise it bounces
  // the user straight back out to the new page on every render.
  const setParams = (buildingId) => {
    const path = location.pathname.includes('staff')
      ? '/dashboard/building-dashboard/maintenance/legacy/staff'
      : '/dashboard/building-dashboard/maintenance/legacy'
    navigate(`${path}/${buildingId}`)
  }

  const getRequestsPageRoute = () => {
    const buildingId = paramsBuildingId
      ? `/${paramsBuildingId}`
      : `/${selectedBuilding ? selectedBuilding.id : '88'}`
    const requestId = paramsRequestId ? `/${paramsRequestId}` : ''
    return `/dashboard/building-dashboard/maintenance/legacy${buildingId}${requestId}`
  }

  useEffect(() => {
    const buildingId = (selectedBuilding && selectedBuilding.id) || paramsBuildingId
    if (paramsBuildingId !== String(buildingId)) {
      setParams(buildingId)
    }
  }, [selectedBuilding])

  return (
    <div className="maintenance-toolbar">
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <button
          className="button button-black"
          onClick={toggleModal}
          style={{ marginLeft: '16px' }}
        >
          {t('maintenance:toolbar.addRequest')}
        </button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        {!isStaffTab && (
          <ToggleSwitch
            falsyLabel={t('maintenance:page.designToggle.old')}
            truthyLabel={t('maintenance:page.designToggle.new')}
            checked={false}
            onChange={handleUIToggle}
          />
        )}
        <div className="maintenance-toolbar__wrapper">
          <Toolbar
            routes={[
              {
                path: getRequestsPageRoute(),
                name: t('maintenance:toolbar.tabs.requests'),
              },
              {
                path: `/dashboard/building-dashboard/maintenance/legacy/staff${
                  paramsBuildingId ? `/${paramsBuildingId}` : ''
                }`,
                name: t('maintenance:toolbar.tabs.staff'),
                hidden: ['Staff'].includes(userRole),
              },
            ]}
          />
        </div>
      </div>
    </div>
  )
}

MaintenanceToolbar.propTypes = {
  toggleModal: PropTypes.func,
}

export default MaintenanceToolbar
